import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Comment from '../comment/Comment';
import './NewsDetail.css';

const NewsDetail = () => {
    const { id } = useParams(); // Get news ID from URL
    const navigate = useNavigate();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [liked, setLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [commentCount, setCommentCount] = useState(0);
    const [showCommentModal, setShowCommentModal] = useState(false);

    // Get device ID
    const getDeviceId = () => {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + Date.now();
            localStorage.setItem('deviceId', deviceId);
        }
        return deviceId;
    };

    useEffect(() => {
        if (id) {
            fetchNewsDetail();
            fetchLikeStatus();
            fetchCommentCount();
        }
    }, [id]);

    const fetchNewsDetail = async () => {
        setLoading(true);
        try {
            // Try LatestNews first
            let res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}api/v1/users/latestNews/${id}`
            ).catch(() => null);

            // If not found, try OldNews
            if (!res || !res.data) {
                res = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}api/v1/users/oldNews/${id}`
                );
            }

            setNews(res.data.data);
        } catch (err) {
            console.error("Error fetching news detail:", err);
            alert("News not found!");
            navigate('/'); // Redirect to home if news not found
        } finally {
            setLoading(false);
        }
    };

    const fetchLikeStatus = async () => {
        try {
            const deviceId = getDeviceId();
            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}api/v1/users/likes/${id}`,
                { params: { deviceId } }
            );
            setLiked(res.data.data.userLiked);
            setLikeCount(res.data.data.totalLikes);
        } catch (err) {
            console.error("Error fetching like status:", err);
        }
    };

    const fetchCommentCount = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}api/v1/users/comments/${id}`
            );
            setCommentCount(res.data.data.totalComments);
        } catch (err) {
            console.error("Error fetching comments:", err);
        }
    };

    const handleLike = async () => {
        try {
            const deviceId = getDeviceId();
            const res = await axios.post(
                `${process.env.REACT_APP_BASE_URL}api/v1/users/like/${id}`,
                { deviceId }
            );
            setLiked(res.data.data.liked);
            setLikeCount(res.data.data.totalLikes);
        } catch (err) {
            console.error("Error toggling like:", err);
        }
    };

    const handleShare = async () => {
        const shareUrl = window.location.href;
        const shareText = news?.title || "Check out this news!";

        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareText,
                    url: shareUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    fallbackShare(shareUrl);
                }
            }
        } else {
            fallbackShare(shareUrl);
        }
    };

    const fallbackShare = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            alert('Link copied to clipboard!');
        } catch (err) {
            alert(`Copy this link: ${url}`);
        }
    };

    const closeCommentModal = () => {
        setShowCommentModal(false);
        fetchCommentCount();
    };

    if (loading) {
        return (
            <div className="news-detail-loading">
                <div className="loading-spinner"></div>
                <p>Loading news...</p>
            </div>
        );
    }

    if (!news) {
        return (
            <div className="news-detail-error">
                <h2>News not found</h2>
                <button onClick={() => navigate('/')}>Go to Home</button>
            </div>
        );
    }

    return (
        <div className="news-detail-container">
            <div className="news-detail-content">
                {/* Back Button */}
                <button className="back-button" onClick={() => navigate(-1)}>
                    ← Back
                </button>

                {/* News Category */}
                <div className="news-category-badge">{news.category || 'News'}</div>

                {/* News Title */}
                <h1 className="news-title">{news.title}</h1>

                {/* News Meta Info */}
                <div className="news-meta">
                    <span className="news-date">
                        📅 {new Date(news.date).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}
                    </span>
                </div>

                {/* News Image */}
                {news.image && (
                    <div className="news-image-container">
                        <img src={news.image} alt={news.title} className="news-image" />
                    </div>
                )}

                {/* News Description/Content */}
                <div className="news-description">
                    <p>{news.description || news.content}</p>
                </div>

                {/* Action Buttons */}
                <div className="news-actions">
                    <button 
                        className={`action-btn like-btn ${liked ? 'liked' : ''}`}
                        onClick={handleLike}
                    >
                        <i className={`fa ${liked ? 'fa-thumbs-up' : 'fa-thumbs-o-up'}`}></i>
                        <span>{likeCount} {likeCount === 1 ? 'Like' : 'Likes'}</span>
                    </button>

                    <button 
                        className="action-btn comment-btn"
                        onClick={() => setShowCommentModal(true)}
                    >
                        <i className="fa fa-comment-o"></i>
                        <span>{commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}</span>
                    </button>

                    <button 
                        className="action-btn share-btn"
                        onClick={handleShare}
                    >
                        <i className="fa fa-share-alt"></i>
                        <span>Share</span>
                    </button>
                </div>
            </div>

            {/* Comment Modal */}
            {showCommentModal && (
                <Comment
                    newsId={id}
                    onClose={closeCommentModal}
                />
            )}
        </div>
    );
};

export default NewsDetail;